import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

// =============================================================================
// Navela admin context
// -----------------------------------------------------------------------------
// Data layer for the Navela admin section. Talks to the FOUR Navela packages on
// the Go backend, each mounted under its own prefix (dual admin/master access):
//
//   vessels          {API}/{admin|master}/navela-vessels/api/v1/vessels
//   ship routes      {API}/{admin|master}/navela-ship-routes/api/v1/ship-routes
//   vessel images    {API}/{admin|master}/navela-vessel-images/api/v1/vessels/{vesselId}/images
//   deletion requests {API}/{admin|master}/navela-account-deletions/api/v1/account-deletions
//
// Notes on the live backend contract:
//   • vessel updates and route updates are PATCH (partial); deletes return 204.
//   • vessel `imo` is an integer; `category` is an enum
//     (cruise|cargo|tanker|passenger|ferry|other); a cruise line may be given by
//     `cruise_line_id` or by `cruise_line` name (upserted).
//   • a route's stops are NOT part of the route PATCH — they are replaced whole
//     via PUT .../ship-routes/{id}/stops  { stops:[...] }.
//   • vessel images are per-vessel Cloudflare uploads: list/upload/delete/
//     set-primary. Upload is multipart (form field "image"). The delivery URL is
//     `cloudflare_image_url`; "primary" means the vessel's photo_url points at it.
// =============================================================================

const NavelaContext = createContext();

export const useNavela = () => {
  const context = useContext(NavelaContext);
  if (!context) {
    throw new Error('useNavela must be used within a NavelaProvider');
  }
  return context;
};

export const NavelaProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://admin-golang-backend-8cdf9b481f22.herokuapp.com';

  // The backend mounts each Navela package under its own top-level prefix, behind
  // both /admin and /master. Pick the scope for the current login.
  const scope = useCallback(() => {
    if (loginType === 'master') return 'master';
    if (loginType === 'admin') return 'admin';
    throw new Error('Invalid login type for Navela access');
  }, [loginType]);

  const vesselsBase = useCallback(() => `${API_BASE_URL}/${scope()}/navela-vessels/api/v1/vessels`, [API_BASE_URL, scope]);
  const routesBase = useCallback(() => `${API_BASE_URL}/${scope()}/navela-ship-routes/api/v1/ship-routes`, [API_BASE_URL, scope]);
  const imagesBase = useCallback((vesselId) => `${API_BASE_URL}/${scope()}/navela-vessel-images/api/v1/vessels/${encodeURIComponent(vesselId)}/images`, [API_BASE_URL, scope]);
  const deletionsBase = useCallback(() => `${API_BASE_URL}/${scope()}/navela-account-deletions/api/v1/account-deletions`, [API_BASE_URL, scope]);
  const metricsBase = useCallback(() => `${API_BASE_URL}/${scope()}/navela-metrics/api/v1/metrics`, [API_BASE_URL, scope]);
  const articlesBase = useCallback(() => `${API_BASE_URL}/${scope()}/navela-news/api/v1/articles`, [API_BASE_URL, scope]);

  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can manage Navela data');
    }
  }, [loginType]);

  const handleApiResponse = async (response) => {
    // 204 No Content (delete endpoints) has an empty body.
    if (response.status === 204) {
      if (!response.ok) throw new Error('Request failed');
      return null;
    }
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
    }
    if (!response.ok) {
      const msg = (data && (data.error || data.message)) || 'Request failed';
      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  const clearError = useCallback(() => setError(null), []);

  // Wrap loading/error handling around one request so each helper stays tiny.
  const run = useCallback(async (fn, fallbackMsg) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      const msg = err.message || fallbackMsg;
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [validateAccess]);

  const buildQuery = (options = {}, keys = []) => {
    const params = new URLSearchParams();
    keys.forEach((k) => {
      const v = options[k];
      if (v !== undefined && v !== null && v !== '') params.append(k, v);
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  const jsonBody = (payload) => ({
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  // ── Vessels ────────────────────────────────────────────────────────────────
  const fetchVessels = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['search', 'category', 'cruise_line_id', 'limit', 'offset']);
    return handleApiResponse(await makeAuthenticatedRequest(`${vesselsBase()}${qs}`));
  }, 'Failed to fetch vessels'), [run, makeAuthenticatedRequest, vesselsBase]);

  const getVessel = useCallback((id) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${vesselsBase()}/${encodeURIComponent(id)}`)),
  'Failed to fetch vessel'), [run, makeAuthenticatedRequest, vesselsBase]);

  const createVessel = useCallback((payload) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(vesselsBase(), { method: 'POST', ...jsonBody(payload) })),
  'Failed to create vessel'), [run, makeAuthenticatedRequest, vesselsBase]);

  // Partial update — the backend endpoint is PATCH.
  const updateVessel = useCallback((id, payload) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${vesselsBase()}/${encodeURIComponent(id)}`, { method: 'PATCH', ...jsonBody(payload) })),
  'Failed to update vessel'), [run, makeAuthenticatedRequest, vesselsBase]);

  const deleteVessel = useCallback((id) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${vesselsBase()}/${encodeURIComponent(id)}`, { method: 'DELETE' })),
  'Failed to delete vessel'), [run, makeAuthenticatedRequest, vesselsBase]);

  const fetchVesselStats = useCallback(() => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${vesselsBase()}/stats`)),
  'Failed to fetch stats'), [run, makeAuthenticatedRequest, vesselsBase]);

  const fetchCruiseLines = useCallback(() => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${vesselsBase()}/cruise-lines`)),
  'Failed to fetch cruise lines'), [run, makeAuthenticatedRequest, vesselsBase]);

  // ── Ship routes ──────────────────────────────────────────────────────────────
  const fetchRoutes = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['vessel_id', 'limit', 'offset']);
    return handleApiResponse(await makeAuthenticatedRequest(`${routesBase()}${qs}`));
  }, 'Failed to fetch routes'), [run, makeAuthenticatedRequest, routesBase]);

  const getRoute = useCallback((id) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${routesBase()}/${encodeURIComponent(id)}`)),
  'Failed to fetch route'), [run, makeAuthenticatedRequest, routesBase]);

  const createRoute = useCallback((payload) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(routesBase(), { method: 'POST', ...jsonBody(payload) })),
  'Failed to create route'), [run, makeAuthenticatedRequest, routesBase]);

  // Voyage fields only — stops are replaced separately (see replaceRouteStops).
  const updateRoute = useCallback((id, payload) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${routesBase()}/${encodeURIComponent(id)}`, { method: 'PATCH', ...jsonBody(payload) })),
  'Failed to update route'), [run, makeAuthenticatedRequest, routesBase]);

  const deleteRoute = useCallback((id) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${routesBase()}/${encodeURIComponent(id)}`, { method: 'DELETE' })),
  'Failed to delete route'), [run, makeAuthenticatedRequest, routesBase]);

  // Replace a voyage's whole itinerary in one call. { stops:[...] }
  const replaceRouteStops = useCallback((id, stops) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${routesBase()}/${encodeURIComponent(id)}/stops`, { method: 'PUT', ...jsonBody({ stops }) })),
  'Failed to save itinerary'), [run, makeAuthenticatedRequest, routesBase]);

  // ── Vessel images (per vessel) ────────────────────────────────────────────────
  const fetchVesselImages = useCallback((vesselId) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(imagesBase(vesselId))),
  'Failed to fetch images'), [run, makeAuthenticatedRequest, imagesBase]);

  // Multipart upload — makeAuthenticatedRequest drops Content-Type for FormData
  // so the browser sets the multipart boundary itself.
  const uploadVesselImage = useCallback((vesselId, file) => run(async () => {
    const form = new FormData();
    form.append('image', file);
    return handleApiResponse(await makeAuthenticatedRequest(imagesBase(vesselId), { method: 'POST', body: form }));
  }, 'Failed to upload image'), [run, makeAuthenticatedRequest, imagesBase]);

  const deleteVesselImage = useCallback((vesselId, imageId) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${imagesBase(vesselId)}/${encodeURIComponent(imageId)}`, { method: 'DELETE' })),
  'Failed to delete image'), [run, makeAuthenticatedRequest, imagesBase]);

  const setPrimaryImage = useCallback((vesselId, imageId) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${imagesBase(vesselId)}/${encodeURIComponent(imageId)}/set-primary`, { method: 'POST' })),
  'Failed to set primary photo'), [run, makeAuthenticatedRequest, imagesBase]);

  // ── News articles ────────────────────────────────────────────────────────────
  // Read/edit/delete the scraped cruise-news feed and toggle the is_duplicate flag
  // the scraper sets on near-identical (syndicated) headlines. List returns
  // { articles, total_count, ... }; update is PATCH (partial, also marks/unmarks a
  // duplicate via { is_duplicate }); delete returns 204.
  const fetchArticles = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['search', 'source_id', 'cruise_line_id', 'is_duplicate', 'is_published', 'limit', 'offset']);
    return handleApiResponse(await makeAuthenticatedRequest(`${articlesBase()}${qs}`));
  }, 'Failed to fetch articles'), [run, makeAuthenticatedRequest, articlesBase]);

  const getArticle = useCallback((id) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${articlesBase()}/${encodeURIComponent(id)}`)),
  'Failed to fetch article'), [run, makeAuthenticatedRequest, articlesBase]);

  const updateArticle = useCallback((id, payload) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${articlesBase()}/${encodeURIComponent(id)}`, { method: 'PATCH', ...jsonBody(payload) })),
  'Failed to update article'), [run, makeAuthenticatedRequest, articlesBase]);

  // Convenience wrapper over updateArticle for the mark/unmark-duplicate action.
  const setArticleDuplicate = useCallback((id, isDuplicate) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${articlesBase()}/${encodeURIComponent(id)}`, { method: 'PATCH', ...jsonBody({ is_duplicate: isDuplicate }) })),
  'Failed to update article'), [run, makeAuthenticatedRequest, articlesBase]);

  const deleteArticle = useCallback((id) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${articlesBase()}/${encodeURIComponent(id)}`, { method: 'DELETE' })),
  'Failed to delete article'), [run, makeAuthenticatedRequest, articlesBase]);

  const fetchArticleStats = useCallback(() => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${articlesBase()}/stats`)),
  'Failed to fetch article stats'), [run, makeAuthenticatedRequest, articlesBase]);

  const fetchNewsSources = useCallback(() => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${articlesBase()}/sources`)),
  'Failed to fetch news sources'), [run, makeAuthenticatedRequest, articlesBase]);

  // ── Account deletion requests ────────────────────────────────────────────────
  // The public webapp deletion form feeds this queue; admins triage it here.
  // List returns { requests, total_count, ... }; status update is PATCH
  // { status, admin_note? }; delete returns 204.
  const fetchDeletionRequests = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['status', 'search', 'limit', 'offset']);
    return handleApiResponse(await makeAuthenticatedRequest(`${deletionsBase()}${qs}`));
  }, 'Failed to fetch deletion requests'), [run, makeAuthenticatedRequest, deletionsBase]);

  const fetchDeletionStats = useCallback(() => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${deletionsBase()}/stats`)),
  'Failed to fetch deletion stats'), [run, makeAuthenticatedRequest, deletionsBase]);

  const updateDeletionStatus = useCallback((id, payload) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${deletionsBase()}/${encodeURIComponent(id)}`, { method: 'PATCH', ...jsonBody(payload) })),
  'Failed to update deletion request'), [run, makeAuthenticatedRequest, deletionsBase]);

  const deleteDeletionRequest = useCallback((id) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${deletionsBase()}/${encodeURIComponent(id)}`, { method: 'DELETE' })),
  'Failed to delete deletion request'), [run, makeAuthenticatedRequest, deletionsBase]);

  // Re-send the user + team notification emails (synchronous on the backend);
  // returns { user_email_sent, team_email_sent, email_error }.
  const resendDeletionEmails = useCallback((id) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${deletionsBase()}/${encodeURIComponent(id)}/resend-emails`, { method: 'POST' })),
  'Failed to resend notification emails'), [run, makeAuthenticatedRequest, deletionsBase]);

  // Perform the ACTUAL account deletion behind a request: removes the Navela user
  // account + their data by the request's email, then marks the request completed.
  // Returns { user_found, message, result: { registrations_deleted, tracked_ships_deleted, other_rows_deleted } }.
  const deleteUserAccount = useCallback((id) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${deletionsBase()}/${encodeURIComponent(id)}/delete-account`, { method: 'POST' })),
  'Failed to delete user account'), [run, makeAuthenticatedRequest, deletionsBase]);

  // ── Usage metrics (read-only) ────────────────────────────────────────────────
  // Reporting over the usage_events log written by the Navela ingestion server.
  // Every helper is a filtered GET; the backend returns the standard {success,data}
  // envelope, so we hand back the `data` payload (falling back to the raw body).
  const unwrap = (res) => (res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res);

  // Shared date-window + platform filters live on almost every report.
  const fetchMetricsSummary = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'platform']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/summary${qs}`)));
  }, 'Failed to fetch metrics summary'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchMetricsFilters = useCallback(() => run(async () =>
    unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/filters`))),
  'Failed to fetch metrics filters'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchMetricsTimeseries = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'interval', 'platform', 'event_name']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/timeseries${qs}`)));
  }, 'Failed to fetch metrics timeseries'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchShipBacklinks = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'kind', 'partner', 'platform', 'limit']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/ships/backlinks${qs}`)));
  }, 'Failed to fetch ship backlinks'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchElementUsage = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'platform', 'screen']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/elements${qs}`)));
  }, 'Failed to fetch element usage'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchScreenViews = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'platform']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/screens${qs}`)));
  }, 'Failed to fetch screen views'), [run, makeAuthenticatedRequest, metricsBase]);

  // Raw event explorer — returns the full { events, total_count, ... } envelope.
  const fetchMetricsEvents = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'event_name', 'platform', 'ship_id', 'screen', 'kind', 'partner', 'search', 'limit', 'offset']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/events${qs}`)));
  }, 'Failed to fetch events'), [run, makeAuthenticatedRequest, metricsBase]);

  const value = {
    loading,
    error,
    clearError,
    // vessels
    fetchVessels, getVessel, createVessel, updateVessel, deleteVessel, fetchVesselStats, fetchCruiseLines,
    // routes
    fetchRoutes, getRoute, createRoute, updateRoute, deleteRoute, replaceRouteStops,
    // images
    fetchVesselImages, uploadVesselImage, deleteVesselImage, setPrimaryImage,
    // news articles
    fetchArticles, getArticle, updateArticle, setArticleDuplicate, deleteArticle, fetchArticleStats, fetchNewsSources,
    // account deletion requests
    fetchDeletionRequests, fetchDeletionStats, updateDeletionStatus, deleteDeletionRequest, resendDeletionEmails, deleteUserAccount,
    // usage metrics (read-only)
    fetchMetricsSummary, fetchMetricsFilters, fetchMetricsTimeseries, fetchShipBacklinks, fetchElementUsage, fetchScreenViews, fetchMetricsEvents,
    // access flags
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasNavelaAccess: loginType === 'admin' || loginType === 'master',
  };

  return (
    <NavelaContext.Provider value={value}>
      {children}
    </NavelaContext.Provider>
  );
};
